import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { type ParsedSuccess } from '../../tool-call-utils';
import { ToolCallError } from '../../ToolCallError';

interface GetListViewsTool {
  name: AgentTools.sf_get_list_views;
  args: { objectName: string; includeRecords?: boolean };
}

export type GetListViewsToolCall = ToolCall<GetListViewsTool['name'], GetListViewsTool['args']>;

export type GetListViewsToolCallSummary = ToolCallSummary<
  GetListViewsTool['name'],
  GetListViewsTool['args']
>;

interface ListViewsResult {
  listViews: Array<{
    id: string;
    developerName: string;
    label: string;
    soqlCompatible: boolean;
    describeUrl: string;
  }>;
  totalSize: number;
}

export function GetListViews({ toolCall }: { toolCall: GetListViewsToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<ListViewsResult>(
    toolCall,
    ({ toolCallState, parsedResult }) => {
      const objectName = toolCall.args.objectName;
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.sf_get_list_views.title.whileRunning', { objectName });
        case ToolCallState.Error:
          return t('toolCalls.sf_get_list_views.title.errors', { objectName });
        case ToolCallState.Success:
          return t('toolCalls.sf_get_list_views.title.success', {
            totalSize: (parsedResult as ParsedSuccess<ListViewsResult>).results.totalSize || 0,
            objectName,
          });
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content:
        !toolCall.isRunning && parsedResult ? (
          parsedResult.hasErrors ? (
            <ToolCallError parsedResult={parsedResult} />
          ) : (
            <ToolCallDataList
              items={parsedResult.results.listViews.map(listView => ({
                label: listView.label,
                value: listView.id,
              }))}
            />
          )
        ) : null,
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
